import React, { useCallback, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'src/redux';

import { Box, Link, Typography } from '@mui/material';
import { BrandLogo, PositionSubscriptionForm } from '../../components';
import { ConfirmationEmailSent } from './components/ConfirmationEmailSent';

import {
  createCandidatePositionSubscription,
  positionSubscriptionSelectors,
} from 'src/redux/positionSubscription';
import { referFriendSelectors } from 'src/redux/referFriend';

import { PositionSubscriptionFields } from 'src/types';
import { PositionSubscriptionType } from 'src/constants';
import { isStatusComplete } from 'src/utils';

import { createCandidatePositionSubscriptionValidationSchema } from './validationsSchema';

export const PositionSubscriptionCandidate: React.FC = () => {
  const dispatch = useAppDispatch();

  const { data: referrerProfile } = useAppSelector(
    referFriendSelectors.getReferrerAPIData,
  );

  const createPositionSubscriptionAPIStatus = useAppSelector(
    positionSubscriptionSelectors.getCreateSubscriptionAPIStatus,
  );
  const subscriptionDetails = useAppSelector(
    positionSubscriptionSelectors.getSubscriptionDetailsAPIData,
  );

  const requiredFields: Array<keyof PositionSubscriptionFields> = useMemo(
    () => ['email', 'name', 'locations', 'technicalFlows', 'experience'],
    [],
  );

  const onPositionSubscriptionFormSubmit = useCallback(
    (values: PositionSubscriptionFields) => {
      dispatch(
        createCandidatePositionSubscription({
          email: values.email!,
          name: values.name!,
          locations: values.locations!,
          technicalFlows: values.technicalFlows!,
          experience: values.experience!,
        }),
      );
    },
    [],
  );

  return (
    <Box
      sx={(theme) => ({
        background: theme.palette.background.backgroundPrimary,
        minWidth: '100%',
        minHeight: '100%',
        padding: '41px 16px',
        [theme.breakpoints.down('lg')]: {
          paddingTop: '130px',
        },
      })}
    >
      <Box sx={{ position: 'absolute', top: '49px', left: '66px' }}>
        <BrandLogo width={133} />
      </Box>
      <Box display="flex" alignItems="center" flexDirection="column">
        <Box
          sx={(theme) => ({
            background: theme.palette.common.white,
            borderRadius: '48px',
            width: { xs: '100%', md: '603px' },
            maxWidth: '603px',
            position: 'relative',
          })}
        >
          {!isStatusComplete(createPositionSubscriptionAPIStatus) ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                padding: { xs: '62px 32px', md: '62px 100px' },
                width: '100%',
              }}
            >
              <PositionSubscriptionForm
                title={
                  <>
                    Get notified about our
                    <br />
                    newest positions
                  </>
                }
                requiredFields={requiredFields}
                validationSchema={
                  createCandidatePositionSubscriptionValidationSchema
                }
                initialValues={{
                  email: referrerProfile?.email || null,
                  name: referrerProfile?.name || null,
                }}
                type={PositionSubscriptionType.Candidate}
                submissionBtnLabel="Subscribe"
                apiStatus={createPositionSubscriptionAPIStatus}
                onSubmit={onPositionSubscriptionFormSubmit}
              />
            </Box>
          ) : (
            <ConfirmationEmailSent
              email={subscriptionDetails.data?.email || ''}
            />
          )}
        </Box>
        <Box display="flex" justifyContent="center" mt={6}>
          <Typography
            variant="body2"
            color="secondary"
            textAlign="center"
            fontSize="0.675rem"
            mx={2}
          >
            <Link component={RouterLink} to="/terms-of-use">
              Terms of use
            </Link>
          </Typography>
          <Typography
            variant="body2"
            color="secondary"
            textAlign="center"
            fontSize="0.675rem"
            mx={2}
          >
            <Link href="https://agileengine.com/privacy-policy" target="_blank">
              Privacy policy
            </Link>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
